import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { TableCellProps } from '@mui/material/TableCell'
import { IconButton, SxProps, Theme, Tooltip } from '@mui/material'
import TablePagination from '@mui/material/TablePagination'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { isNil } from 'lodash-es'
import { useLocation, useNavigate } from 'react-router-dom'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import DataTable from '../table/DataTable'
import { DataTableFilterOption } from '../table/DataTable.types'

const DATE_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}|^\d{2}\/\d{2}\/\d{4}/

export type TableItemId = string | number
export type TableItem = Record<string, unknown> & { id?: TableItemId }

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  if (b[orderBy] < a[orderBy]) return -1
  if (b[orderBy] > a[orderBy]) return 1
  return 0
}

export interface ActionButton<T> {
  type: (value: T) => React.ReactNode
  label: string
  action: (value: T) => void
  color?: 'default' | 'inherit' | 'primary' | 'secondary'
  testId?: string
}

export type PaginationProps = React.ComponentProps<typeof TablePagination>

export interface DatatableSelectionProps<T extends TableItem> {
  onSelect: (item: T) => void
  selectedIds: Array<T['id']>
  setSelectedIds: Dispatch<SetStateAction<Array<T['id']>>>
}

export interface DatatableProps<T extends TableItem> {
  sx?: SxProps<Theme>
  headers: readonly DatatableHeaderProps<keyof T>[]
  secondHeaders?: T
  data: T[]
  translateKey: string
  actionsButtons?: Array<ActionButton<T>>
  enablePagination?: boolean
  enableSelection?: DatatableSelectionProps<T>
  navigateOnClick?: boolean
  onClick?: (item: T) => void
  orderByDefault?: keyof T
  orderByOrder?: 'asc' | 'desc'
}

export interface DatatableHeaderProps<V> extends TableCellProps {
  name: V
  headerKey?: string
  renderFn?: (item: any) => JSX.Element
  renderEmptyFn?: (item: any) => JSX.Element | undefined
  canClick?: boolean
}

const Datatable = <T extends TableItem>({
  sx,
  headers,
  data,
  translateKey,
  actionsButtons,
  navigateOnClick = false,
  onClick,
  enablePagination,
  enableSelection,
  orderByDefault,
  orderByOrder,
}: DatatableProps<T>): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const t = useAppTranslations()

  const columns = useMemo<ColumnDef<T, any>[]>(() => {
    const columnHelper = createColumnHelper<T>()
    const visibleHeaders = headers.filter((header) => header.name !== 'actions')
    const dataColumns = visibleHeaders.map((header) => {
      const values = data.map((row) => row[header.name]).filter((value) => value !== null && value !== undefined)
      const isNumericColumn = values.length > 0 && values.every((value) => typeof value === 'number')
      const isDateColumn =
        values.length > 0 && values.every((value) => typeof value === 'string' && DATE_VALUE_PATTERN.test(value))
      const filterOptions = Array.from(new Set(values.map((value) => String(value)))).map<DataTableFilterOption>(
        (value) => ({
          label: value,
          value,
        })
      )

      const headerLabel = t(header.headerKey ?? translateKey + String(header.name))
      return columnHelper.accessor((row) => row[header.name], {
        id: String(header.name),
        header: headerLabel,
        cell: ({ row }) => {
          const item = row.original
          const value = item[header.name]
          if (isNil(value)) return header.renderEmptyFn?.(item) ?? 'n/a'
          if (header.renderFn) return header.renderFn(item)
          if (typeof value === 'boolean') return value ? <CheckBox /> : <CheckBoxOutlineBlank />
          return value as React.ReactNode
        },
        meta: {
          cellSx: header.sx,
          columnLabel: headerLabel,
          enableFilter: true,
          enableNullFilter: data.some((row) => row[header.name] === null || row[header.name] === undefined),
          filterOptions: !isNumericColumn && !isDateColumn ? filterOptions : undefined,
          filterVariant: isNumericColumn ? 'number' : isDateColumn ? 'date' : 'select',
          headerSx: header.align ? { textAlign: header.align } : undefined,
        },
        sortingFn: (rowA, rowB) => descendingComparator(rowB.original, rowA.original, header.name),
      })
    })

    if (!actionsButtons && !enableSelection) return dataColumns

    return [
      ...dataColumns,
      columnHelper.display({
        id: 'actions',
        header: t('commons.actionsTitle'),
        cell: ({ row }) => (
          <>
            {actionsButtons?.map((button, i) => (
              <Tooltip key={i} title={button.label}>
                <IconButton
                  data-testid={button.testId}
                  onClick={() => {
                    button.action(row.original)
                  }}
                  size="small"
                >
                  {button.type(row.original)}
                </IconButton>
              </Tooltip>
            ))}
            {enableSelection?.onSelect && (
              <IconButton
                onClick={() => enableSelection.onSelect(row.original)}
                size="small"
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
              >
                {enableSelection.selectedIds?.includes(row.original?.id) ? <CheckBox /> : <CheckBoxOutlineBlank />}
              </IconButton>
            )}
          </>
        ),
        enableSorting: false,
        meta: { hideable: false },
      }),
    ]
  }, [actionsButtons, data, enableSelection, headers, t, translateKey])

  const handleRowClick = useMemo(() => {
    if (!onClick && !navigateOnClick) return undefined
    return (item: T): void => {
      if (onClick) onClick(item)
      if (navigateOnClick) navigate(`${location.pathname}/${item.id}`)
    }
  }, [location.pathname, navigate, navigateOnClick, onClick])

  const defaultSorting = useMemo(
    () => [{ desc: orderByOrder === 'desc', id: String(orderByDefault ?? 'firstname') }],
    [orderByDefault, orderByOrder]
  )

  return (
    <DataTable
      columns={columns}
      data={data}
      defaultSorting={defaultSorting}
      enablePagination={enablePagination}
      onClick={handleRowClick}
      sx={sx}
      withContainer={false}
    />
  )
}

export default Datatable
